import * as React from "react";
import {
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { DayOfWeekEnum } from "../../../API";
import { useMemo } from "react";

type WeekdayPillsProps = Omit<
  ToggleButtonGroupProps,
  "value" | "defaultValue"
> & {
  value?: DayOfWeekEnum[];
  disabled?: boolean;
  defaultValue?: DayOfWeekEnum[];
  onChange?: (days: DayOfWeekEnum[]) => void;
  disabledDays?: DayOfWeekEnum[];
  startOfWeek?: DayOfWeekEnum.SUNDAY | DayOfWeekEnum.MONDAY;
};

const Pill = styled(ToggleButton)(({ theme }) => {
  return {
    border: "none",
    borderRadius: "0.25rem",
    padding: 0,
    width: "2rem",
    height: "2rem",
    lineHeight: 1,
    fontWeight: 500,
    backgroundColor: theme.palette.otherDivider.main,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.otherStandardInputLine.main,
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    "&.Mui-disabled": {
      opacity: 0.5,
      color: theme.palette.text.disabled,
      backgroundColor: theme.palette.otherStandardInputLine.main,
    },

    "&.Mui-disabled.Mui-selected": {
      opacity: 0.5,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
  };
});

export function WeekdayPills({
  value,
  defaultValue = [],
  onChange,
  disabledDays = [],
  startOfWeek = DayOfWeekEnum.SUNDAY,
  disabled = false,
  ...groupProps
}: WeekdayPillsProps) {
  const isControlled = value !== undefined;
  const [internal, setInternal] = React.useState<DayOfWeekEnum[]>(defaultValue);
  const selected = isControlled ? (value as DayOfWeekEnum[]) : internal;

  const handleChange = (
    _: React.MouseEvent<HTMLElement>,
    newVal: DayOfWeekEnum[]
  ) => {
    if (!isControlled) setInternal(newVal);

    onChange?.(newVal);
  };

  const DAYS = useMemo(() => {
    const result = [
      {
        label: String(DayOfWeekEnum.SUNDAY).charAt(0),
        value: DayOfWeekEnum.SUNDAY,
      },
      {
        label: String(DayOfWeekEnum.MONDAY).charAt(0),
        value: DayOfWeekEnum.MONDAY,
      },
      {
        label: String(DayOfWeekEnum.TUESDAY).charAt(0),
        value: DayOfWeekEnum.TUESDAY,
      },
      {
        label: String(DayOfWeekEnum.WEDNESDAY).charAt(0),
        value: DayOfWeekEnum.WEDNESDAY,
      },
      {
        label: String(DayOfWeekEnum.THURSDAY).charAt(0),
        value: DayOfWeekEnum.THURSDAY,
      },
      {
        label: String(DayOfWeekEnum.FRIDAY).charAt(0),
        value: DayOfWeekEnum.FRIDAY,
      },
      {
        label: String(DayOfWeekEnum.SATURDAY).charAt(0),
        value: DayOfWeekEnum.SATURDAY,
      },
    ];

    if (startOfWeek === DayOfWeekEnum.MONDAY) {
      const d = result.shift();

      if (d) {
        result.push(d);
      }
    }

    return result;
  }, [startOfWeek]);

  return (
    <Box display="flex">
      <ToggleButtonGroup
        value={selected}
        onChange={handleChange}
        exclusive={false}
        {...groupProps}
        sx={{
          gap: 1,
          "& .MuiToggleButtonGroup-grouped": {
            margin: 0,
            border: 0,
            borderRadius: "0.25rem",
          },
        }}
      >
        {DAYS.map((d, idx) => (
          <Pill
            key={`${d.label}-${idx}`}
            value={d.value}
            disabled={disabledDays.includes(d.value) || disabled}
            aria-label={d.label}
          >
            {d.label}
          </Pill>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}
